import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorService } from '@common/services/error.service';
import { ModalService } from '@common/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { ToastService } from '@services/toast.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private toastyService: ToastService,
        private translateService: TranslateService,
        private dialogService: ModalService,
        private errorService: ErrorService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error instanceof HttpErrorResponse) {
                    // server error
                    if (error.status === 500) {
                        // Handle stale session errors (NHibernate StaleObjectStateException for UserSession)
                        const errorMessage = error.error?.errorMessage || error.message || '';
                        if (errorMessage.includes('StaleObjectStateException') && 
                            errorMessage.includes('UserSession')) {
                            const message = this.translateService.instant('Your session has expired. The page will reload.');
                            this.toastyService.warning(message, '', { timeOut: 3000 });
                            
                            if (!environment.production) {
                                console.warn('Stale UserSession detected - clearing and reloading', error);
                            }
                            
                            // Clear storage and reload to get fresh session
                            setTimeout(() => {
                                localStorage.clear();
                                sessionStorage.clear();
                                window.location.reload();
                            }, 1000);
                            
                            return throwError(() => error);
                        }
                        
                        if (error.error?.isPcsException) {
                            this.toastyService.error(error.error.errorMessage);
                            if (!environment.production) console.error(error);
                            return throwError(() => error);
                        }
                        this.dialogService.showErrorDialog(error);
                    }
                    // for validation errors
                    if (
                        error.error?.status === 400 &&
                        error.error?.title.includes('One or more validation errors occurred.')
                    ) {
                        if (error.error?.errors) {
                            const errors = error.error.errors;
                            for (const key in errors) {
                                const element = errors[key];
                                this.toastyService.error(element);
                            }
                        } else {
                            this.toastyService.error(error.error.errorMessage);
                        }
                        if (!environment.production) console.error(error);
                    } else if (error.status === 400) {
                        if (error.error?.validationErrors?.length > 0) {
                            this.dialogService.showErrorDialog(error, error.error?.validationErrors);
                        } else {
                            this.dialogService.showErrorDialog(error);
                        }
                        if (!environment.production) console.error(error);
                    }
                    // not found
                    if (error.status === 404) {
                        this.toastyService.error(error.error?.errorMessage);
                        if (!environment.production) console.error(error);
                        this.router.navigate(['/home']);
                        return throwError(() => error);
                    }

                    if (error.status === 403) {
                        const message = this.translateService.instant(
                            'Access to this page is restricted. Please contact your administrator if you believe you should have access to this page'
                        );
                        const title = this.translateService.instant('Restricted Access');
                        this.toastyService.error(message, title);

                        if (!environment.production) console.error(error);
                        this.router.navigate(['/home']);
                        return throwError(() => error);
                    }

                    // request body over the server limit, typically too many or too large attachments
                    if (error.status === 413) {
                        const message = this.translateService.instant(
                            'The request is too large. Please attach fewer or smaller files.'
                        );
                        this.toastyService.error(message);

                        if (!environment.production) console.error(error);
                        return throwError(() => error);
                    }

                    // intercept refresh token expire error
                    if (error?.error?.error === 'invalid_grant') {
                        window.location.reload();
                    }

                    if (error.status == 401 && error.url.indexOf('authentication/login') != -1) {
                        if (error?.error?.errorMessage.includes('BEA')) {
                            const errorMessages = this.errorService.getServerMessage(error);
                            this.errorService.displayErrorInToasty(errorMessages);
                            if (!environment.production) console.error(error);
                        }
                        return throwError(() => error);
                    }

                    // unaouthorized but not token expired and not current user
                    // expired tokens are handled in http-interceptor
                    // current user is checked in startup
                    if (
                        error.status == 401 &&
                        !error.headers.has('Token-Expired') &&
                        error.url.indexOf('authentication/getCurrentUser') == -1
                    ) {
                        // reload app, it will check user again
                        window.location.reload();
                    }
                }
                return throwError(() => error);
            })
        );
    }
}
